import { motion } from "framer-motion";
import { Calendar, Coffee, Flag, ArrowRight } from "lucide-react";
import { WEEKS, LINKS } from "../data/content";
import { Reveal, Stagger, StaggerItem } from "./Reveal";

const overview = [
  { label: "Week 1", dates: "4 Aug – 8 Aug", off: false },
  { label: "Week 2", dates: "10 Aug – 15 Aug", off: false },
  { label: "Sunday", dates: "16 Aug · OFF", off: true },
  { label: "Week 3", dates: "17 Aug – 22 Aug", off: false },
  { label: "Sunday", dates: "23 Aug · OFF", off: true },
  { label: "Week 4", dates: "24 Aug – 29 Aug", off: false },
  { label: "Sunday", dates: "30 Aug · OFF", off: true },
  { label: "Week 5", dates: "31 Aug – 5 Sept", off: false },
  { label: "Finale", dates: "7 Sept · Day 30", finale: true },
];

const Timetable = () => (
  <section
    id="timetable"
    data-testid="timetable-section"
    className="relative bg-white py-24 md:py-32 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <Reveal>
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#58cc02]">
          The 30-Day Challenge · Premium Schedule
        </p>
        <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight mt-4 text-[#111827]">
          Your roadmap to a topper-level essay.
        </h2>
        <p className="text-lg text-[#4b5563] max-w-2xl mt-5">
          A rhythm designed so you actually stick with it. Sundays reserved for
          revision &amp; reflection — because rest is part of the plan.
        </p>
      </Reveal>

      {/* Duolingo-style node path */}
      <div className="relative mt-16">
        <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-4">
          {overview.map((o, i) => (
            <StaggerItem key={i}>
              <div
                data-testid={`timeline-node-${i + 1}`}
                className={`relative rounded-3xl p-5 h-full flex flex-col items-center text-center transition-transform duration-300 hover:-translate-y-2 ${
                  o.finale
                    ? "bg-[#58cc02] text-white shadow-[0_12px_30px_rgba(88,204,2,0.35)]"
                    : o.off
                      ? "bg-[#f8f9fa] border-2 border-dashed border-[#d1d5db] text-[#4b5563]"
                      : "bg-[#0a0a0a] text-white"
                }`}
              >
                <div
                  className={`h-11 w-11 rounded-full flex items-center justify-center mb-3 ${
                    o.finale
                      ? "bg-white/20"
                      : o.off
                        ? "bg-white"
                        : "bg-[#58cc02]"
                  }`}
                >
                  {o.finale ? (
                    <Flag className="h-5 w-5 text-white" />
                  ) : o.off ? (
                    <Coffee className="h-5 w-5 text-[#4b5563]" />
                  ) : (
                    <Calendar className="h-5 w-5 text-white" />
                  )}
                </div>
                <span className="font-display font-extrabold text-lg leading-none">
                  {o.label}
                </span>
                <span
                  className={`mt-2 text-xs font-semibold ${
                    o.off ? "text-[#4b5563]" : "text-white/70"
                  } ${o.finale ? "text-white/90" : ""}`}
                >
                  {o.dates}
                </span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>

      {/* Detailed table */}
      <Reveal delay={0.1}>
        <div className="mt-16 rounded-3xl overflow-hidden border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
          <div className="hidden md:grid grid-cols-12 bg-[#0a0a0a] text-white font-display font-bold text-sm uppercase tracking-wide">
            <div className="col-span-2 px-6 py-4">Week</div>
            <div className="col-span-4 px-6 py-4">Dates</div>
            <div className="col-span-2 px-6 py-4">Sessions</div>
            <div className="col-span-4 px-6 py-4">Theme</div>
          </div>
          {WEEKS.map((w, i) => (
            <div
              key={i}
              data-testid={`timetable-row-${i + 1}`}
              className={`grid grid-cols-1 md:grid-cols-12 items-center border-t border-black/5 ${
                w.off
                  ? "bg-[#f8f9fa]"
                  : w.finale
                    ? "bg-[#e5f7d6]"
                    : "bg-white hover:bg-[#f8f9fa] transition-colors"
              }`}
            >
              <div className="col-span-2 px-6 py-5 flex items-center gap-2">
                <span
                  className={`font-display font-extrabold text-lg ${
                    w.finale ? "text-[#46a302]" : w.off ? "text-[#9ca3af]" : "text-[#111827]"
                  }`}
                >
                  {w.week}
                </span>
              </div>
              <div className="col-span-4 px-6 md:py-5 pb-1 md:pb-5 font-semibold text-[#4b5563]">
                {w.dates}
              </div>
              <div className="col-span-2 px-6 md:py-5 pb-1 md:pb-5 font-bold text-[#111827]">
                {w.days}
              </div>
              <div
                className={`col-span-4 px-6 pb-5 md:py-5 font-semibold ${
                  w.off ? "text-[#9ca3af] italic" : "text-[#111827]"
                }`}
              >
                {w.theme}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl bg-[#0a0a0a] text-white p-8 md:p-10">
          <div>
            <p className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">
              Your seat is waiting. The koala isn&apos;t.
            </p>
            <p className="text-white/70 mt-2">
              Lock your spot for ₹1,100 before the doors open on 4 August.
            </p>
          </div>
          <a
            href={LINKS.enrol}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="timetable-enrol-button"
            className="btn-3d group shrink-0 bg-[#58cc02] text-white font-extrabold text-base md:text-lg px-8 py-4 rounded-2xl uppercase tracking-wide flex items-center gap-2"
          >
            Enrol Now
            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Timetable;
